import React, { useEffect, useState } from 'react';

import http from '../../../api/http';
import PageHero from '../../../components/page/PageHero';

const CadenceRunCard = ({ run }) => {
  const packet = run.packet || {};
  const missing = packet.due?.weeklyUpdatesMissing || [];
  const overdue = packet.due?.overdueTasks || [];
  const drift = packet.healthDrift || [];
  const assumptions = packet.due?.assumptionsToReview || [];
  const activity = packet.activity?.events || [];

  return (
    <article className="card stack">
      <div className="row-between">
        <div>
          <h2 className="section-title">{new Date(run.generatedAt).toLocaleString()}</h2>
          <p className="small-muted">Period ending {new Date(packet.period?.end || run.periodEnd).toLocaleString()}</p>
        </div>
      </div>
      <div className="card-grid metrics-grid">
        <div className="card metric-card"><span className="hero-stat-label">Missing updates</span><strong>{missing.length}</strong></div>
        <div className="card metric-card"><span className="hero-stat-label">Overdue tasks</span><strong>{overdue.length}</strong></div>
        <div className="card metric-card"><span className="hero-stat-label">Health drift</span><strong>{drift.length}</strong></div>
        <div className="card metric-card"><span className="hero-stat-label">Recent changes</span><strong>{activity.length}</strong></div>
        <div className="card metric-card"><span className="hero-stat-label">Assumptions to review</span><strong>{assumptions.length}</strong></div>
      </div>
      {missing.length ? (
        <section>
          <h3 className="section-title">Weekly updates missing</h3>
          <ul>{missing.map((item) => <li key={item.sprintPackId}>{item.name || `SprintPack ${item.sprintPackId}`}</li>)}</ul>
        </section>
      ) : null}
      {overdue.length ? (
        <section>
          <h3 className="section-title">Overdue tasks</h3>
          <ul>{overdue.map((item) => <li key={item.id}>{item.title || `Task ${item.id}`} — due {item.dueDate}</li>)}</ul>
        </section>
      ) : null}
      {assumptions.length ? (
        <section>
          <h3 className="section-title">Assumptions to review</h3>
          <ul>{assumptions.map((item) => (
            <li key={item.id}>
              {item.statement || `Assumption ${item.id}`} — {item.status}
              {item.reviewBy ? `, review by ${item.reviewBy}` : ''}
              {item.lastEvidence ? ` · ${item.lastEvidence}` : ''}
            </li>
          ))}</ul>
        </section>
      ) : null}
      {drift.length ? (
        <section>
          <h3 className="section-title">Health drift</h3>
          <ul>{drift.map((item) => (
            <li key={`${item.section}:${item.id}`}>
              {item.label || `${item.section} ${item.id}`} — declared {item.declaredStatus}, evidenced {item.evidencedStatus}
            </li>
          ))}</ul>
        </section>
      ) : null}
      {packet.activity?.truncated ? <p className="small-muted">Activity list capped at 100 events.</p> : null}
    </article>
  );
};

const CadencesPage = () => {
  const [cadences, setCadences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    http.get('/cadences')
      .then(({ data }) => {
        if (active) setCadences(Array.isArray(data?.data) ? data.data : []);
      })
      .catch((requestError) => {
        if (active) setError(requestError?.response?.data?.message || requestError.message || 'Unable to load review packets.');
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  return (
    <div className="stack">
      <PageHero
        title="Review cadence"
        subtitle="A snapshot of missing updates, overdue work, health drift and recent changes for each review rhythm."
      />
      {loading ? <p>Loading review packets...</p> : null}
      {error ? <p className="error-text" role="alert">{error}</p> : null}
      {!loading && !error && !cadences.length ? <p>No active review cadences are configured.</p> : null}
      {cadences.map((cadence) => (
        <section className="stack" key={cadence.id}>
          <div>
            <h2 className="section-title">{cadence.name}</h2>
            <p className="small-muted">Weekly, {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][cadence.weekday]} at {String(cadence.runHourUtc).padStart(2, '0')}:{String(cadence.runMinuteUtc).padStart(2, '0')} UTC</p>
          </div>
          {cadence.runs?.length
            ? cadence.runs.map((run) => <CadenceRunCard key={run.id} run={run} />)
            : <p className="small-muted">No review packet has been generated yet. The scheduler must be enabled for scheduled runs.</p>}
        </section>
      ))}
    </div>
  );
};

export default CadencesPage;
